import { Database, Tables } from "@punchup/data/supabase.types";

import { Comedian } from "./comedian";
import { SupabaseClient } from "@supabase/supabase-js";
import dayjs from "dayjs";
import { getAllSpecialsForComedian } from "./specials";

export interface Metrics {}

export type ComedianRanking = {
  ds: string;
  comedian_id: string;
  score: number;
};

export async function getSignups(
  supabase: SupabaseClient<Database>,
  loggedInComedianId: string,
  page: number,
  pageSize: number = 10000 // Default page size set to 10,000
): Promise<Array<{ created_at: string | null }>> {
  try {
    const rangeStart = (page - 1) * pageSize;
    const rangeEnd = rangeStart + pageSize - 1;

    const { data, error } = await supabase
      .from("user_comedian_follows")
      .select("created_at")
      .eq("comedian_id", loggedInComedianId)
      .range(rangeStart, rangeEnd);

    if (error) {
      console.error("Failed to fetch signups: ", error.message);
      throw new Error("Error fetching signups: " + error.message);
    }

    return data ?? []; // Return an empty array if data is null
  } catch (error) {
    console.error("An unexpected error occurred: ", error);
    throw error; // Rethrow the caught error
  }
}

export async function getSignupsCount(
  supabase: SupabaseClient<Database>,
  loggedInComedianId: string
): Promise<number | null> {
  try {
    const { count, error } = await supabase
      .from("user_comedian_follows")
      .select("created_at", { count: "exact", head: true })
      .eq("comedian_id", loggedInComedianId);

    if (error) {
      console.error("Failed to fetch signups: ", error.message);
      throw new Error(
        `Error fetching signups for comedian with ID ${loggedInComedianId}: ${error.message}`
      );
    }

    if (!count) {
      console.error("No count received from the server.");
      throw new Error("Count not received from server when fetching signups.");
    }

    return count;
  } catch (error) {
    console.error("An unexpected error occurred: ", error);
    throw new Error("Unexpected error while fetching signups: " + error);
  }
}

export async function getPlays(
  supabase: SupabaseClient<Database>,
  comedianSlug: string,
  timeStart: string,
  page: number,
  pageSize: number = 10000 // default page size set to 10,000
): Promise<any[]> {
  try {
    const rangeStart = (page - 1) * pageSize;
    const rangeEnd = rangeStart + pageSize - 1;

    const { data, error } = await supabase
      .from("events_clicked_play")
      .select("*")
      .ilike("$current_url", `%${comedianSlug}%`)
      .gte("timestamp", timeStart)
      .range(rangeStart, rangeEnd);

    if (error) {
      throw new Error(error.message);
    }
    return data ?? [];
  } catch (error) {
    console.error("An unexpected error occurred: ", error);
    throw error; // rethrow the caught error
  }
}

export async function getTickets(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  timeStart: string,
  page: number,
  pageSize: number = 10000 // default page size set to 10,000
): Promise<any[]> {
  try {
    const rangeStart = (page - 1) * pageSize;
    const rangeEnd = rangeStart + pageSize - 1;

    const { data, error } = await supabase
      .from("events_tickets_merged")
      .select("*")
      .eq("ComedianId", comedianId)
      .gte("timestamp", timeStart)
      .range(rangeStart, rangeEnd);

    // Handle potential errors
    if (error) {
      console.error("Failed to fetch ticket events: ", error.message);
      throw new Error("Error fetching ticket events: " + error.message);
    }

    return data ?? []; // Return an empty array if data is null
  } catch (error) {
    console.error("An unexpected error occurred: ", error);
    throw error; // Rethrow the caught error
  }
}

interface VenueData {
  show_id: number;
  venue_name: string;
  show_date: string;
  show_time: string;
  venue_address: string;
  venue_postal_code: string;
  venue_country: string;
  venue_city: string;
  venue_state: string;
  venue_location: {
    latitude: number;
    longitude: number;
  };
}

export async function fetchComedianVenueData(
  comedianId: string,
  supabase: SupabaseClient
): Promise<any[] | null> {
  if (!comedianId) {
    console.error("Comedian data is not available");
    return null;
  }

  try {
    const { data, error } = await supabase
      .from("comedian_shows")
      .select(
        `
        venue_id,
        datetime,
        venues (
          id,
          name,
          address,
          postal_code,
          country,
          city,
          state,
          location
        )
      `
      )
      .eq("comedian_id", comedianId)
      .not("datetime", "is", null)
      .order("datetime", { ascending: true });
    //.limit(1);
    if (error) {
      console.error("Error fetching comedian shows:", error);
      return null;
    }

    const transformedData: VenueData[] = data.map((item: any) => {
      // Format the date and time
      const date = dayjs(item.datetime).format("YYYY-MM-DD"); // Formats the date in YYYY-MM-DD format
      const time = dayjs(item.datetime).format("HH:mm:ss"); // Formats the time in HH:mm:ss format
      return {
        show_id: item.venues.id,
        venue_name: item.venues.name,
        show_date: date, // Use the extracted date
        show_time: time, // Use the extracted time
        venue_address: item.venues.address,
        venue_postal_code: item.venues.postal_code,
        venue_country: item.venues.country,
        venue_city: item.venues.city,
        venue_state: item.venues.state,
        venue_location: item.venues.location,
      };
    });
    return transformedData;
  } catch (error) {
    console.error("An error occurred while fetching data:", error);
    return null;
  }
}

export async function getArtistMetrics(
  comedianId: string,
  supabase: SupabaseClient<Database>
) {
  const { data, error } = await supabase
    .from("artist_metrics")
    .select("*")
    .eq("comedian_id", comedianId);

  if (error) {
    console.error("Failed to fetch artist metrics: ", error);
    throw new Error("Failed to fetch artist metrics: " + error);
  }

  return data;
}

export async function calculateRankingScore(
  comedianId: string,
  comedian: Comedian,
  supabase: SupabaseClient<Database>,
  applyRandomDiversityFactor: boolean = true
) {
  const today = dayjs();
  const comedianSpecials = await getAllSpecialsForComedian(comedianId);
  const artistMetrics = await getArtistMetrics(comedianId, supabase);
  const subscriberCount = artistMetrics[0]?.subscriber_count ?? 0;

  const mostRecentSpecial = comedianSpecials.reduce((prev, current) => {
    const prevDate = dayjs(prev.created_at);
    const currentDate = dayjs(current.created_at);
    return prevDate.isAfter(currentDate) ? prev : current;
  });

  const daysSinceLastUpload = today.diff(
    dayjs(mostRecentSpecial.created_at),
    "days"
  );
  const totalContentUploaded = comedianSpecials.length;
  const daysSinceJoined = today.diff(dayjs(comedian.published_at), "days");

  // Subscriber score (kept the same)
  const subscriberScore = Math.log(subscriberCount + 1) * 2500;

  // Content frequency score (kept the same)
  const contentFrequencyScore = Math.log(totalContentUploaded + 1) * 1000;

  // Recency score (increased boost for recent uploads)
  const recencyScore = Math.max(0, 5000 - daysSinceLastUpload * 10);

  // New comedian boost (significantly increased and decays over 2 weeks)
  const newComedianBoost =
    daysSinceJoined <= 14
      ? 50000 - daysSinceJoined * (50000 / 14) // Linear decay over 14 days
      : 0;

  const diversityFactor = applyRandomDiversityFactor ? Math.random() * 5000 : 0;

  // Calculate final score and floor it to ensure it's an integer
  const finalScore = Math.floor(
    subscriberScore +
      contentFrequencyScore +
      recencyScore +
      newComedianBoost +
      diversityFactor
  );

  return finalScore;
}

export async function insertOrUpdateComedianRankings(
  supabase: SupabaseClient<Database>,
  comedianRankingCollection: Array<ComedianRanking>,
  tableName: "artist_ranking_score_v2"
) {
  if (comedianRankingCollection.length === 0) {
    return null;
  }

  const { data, error } = await supabase
    .from(tableName)
    .upsert(comedianRankingCollection);

  if (error) {
    console.error("Failed to insert or update comedian ranking: ", error);
    throw new Error("Failed to insert or update comedian ranking: " + error);
  }

  return data;
}

export async function getLatestComedianRankDS(
  supabase: SupabaseClient<Database>
) {
  const { data, error } = await supabase
    .from("artist_ranking_score_v2")
    .select("ds")
    .order("ds", { ascending: false })
    .limit(1);

  if (error) {
    console.error("Failed to fetch latest comedian ranking DS: ", error);
    throw new Error("Failed to fetch latest comedian ranking DS: " + error);
  }

  return data[0].ds;
}

export type ComedianRanks = Tables<"artist_ranking_score_v2">;

export async function getComediansRanks(supabase: SupabaseClient<Database>) {
  const ds = await getLatestComedianRankDS(supabase);
  const { data, error } = await supabase
    .from("artist_ranking_score_v2")
    .select("*")
    .eq("ds", ds)
    .order("score", { ascending: false });

  if (error) {
    console.error("Failed to fetch comedian rankings: ", error);
    throw new Error("Failed to fetch comedian rankings: " + error);
  }

  return data;
}

export interface VideoRanking {
  ds: string;
  video_id: string;
  score: number;
}

export async function calculateVideoRankingScore(
  videoId: string,
  supabase: SupabaseClient<Database>
): Promise<number> {
  try {
    // Fetch video metrics
    const { data: videoMetrics, error: metricsError } = await supabase
      .from("video_metrics")
      .select("*")
      .eq("video_id", videoId)
      .single();

    if (metricsError) {
      console.error(
        `Failed to fetch video metrics for ID ${videoId}: ${metricsError.message}`
      );
      return 0;
    }

    if (!videoMetrics) {
      console.warn(`No metrics found for video ID: ${videoId}`);
      return 0;
    }

    // Fetch the special data to get the published_at date
    const { data: special, error: specialError } = await supabase
      .from("specials")
      .select("published_at")
      .eq("id", videoId)
      .single();

    if (specialError) {
      console.error(
        `Failed to fetch special for video ID ${videoId}: ${specialError.message}`
      );
      return 0;
    }

    if (!special || !special.published_at) {
      console.warn(`No published date found for video ID: ${videoId}`);
      return 0;
    }

    const clickedPlayCount = videoMetrics.clicked_play_count ?? 0;
    const daysSincePublished = dayjs().diff(
      dayjs(special.published_at),
      "days"
    );

    // Play count score (increased weight for popular videos)
    const playCountScore = Math.log(clickedPlayCount + 1) * 500;

    // Recency score
    let recencyScore: number;
    if (daysSincePublished <= 1) {
      // Extreme boost for videos less than 24 hours old
      recencyScore = 60000;
    } else if (daysSincePublished <= 3) {
      // Very high boost for videos 1-3 days old
      recencyScore = 45000 - (daysSincePublished - 1) * 5000;
    } else if (daysSincePublished <= 7) {
      // High boost for videos 3-7 days old
      recencyScore = 30000 - (daysSincePublished - 3) * 2500;
    } else if (daysSincePublished <= 14) {
      // Moderate boost for videos 1-2 weeks old
      recencyScore = 20000 - (daysSincePublished - 7) * 1000;
    } else if (daysSincePublished <= 30) {
      // Lower boost for videos 2-4 weeks old
      recencyScore = 13000 - (daysSincePublished - 14) * 300;
    } else {
      // Minimal recency score for older videos
      recencyScore = Math.max(0, 5000 - (daysSincePublished - 30) * 50);
    }

    // Popularity factor (increased impact for very popular videos)
    const popularityFactor = Math.min(clickedPlayCount / 40, 2.5); // Caps at 2.5 for videos with 100+ plays

    // Diversity factor (kept the same)
    const diversityFactor = Math.random() * 200;

    // Calculate final score with increased weight on popularity
    const finalScore = Math.floor(
      playCountScore +
        recencyScore * (1 - popularityFactor * 0.15) + // Recency score decreases even less as popularity increases
        popularityFactor * 18000 + // Further increased boost for popular videos
        diversityFactor
    );

    return finalScore;
  } catch (error) {
    console.error(
      `Unexpected error calculating ranking for video ID ${videoId}:`,
      error
    );
    return 0;
  }
}

export async function getVideosRanks(supabase: SupabaseClient<Database>) {
  const { data: maxDs } = await supabase
    .from("video_ranking_score")
    .select("ds")
    .order("ds", { ascending: false })
    .limit(1)
    .single()
    .throwOnError();

  if (!maxDs?.ds) {
    throw new Error(
      "[getVideosRank] Could not retrieve the time of the most recent update"
    );
  }

  const { data, error } = await supabase
    .from("video_ranking_score")
    .select("*")
    .eq("ds", maxDs.ds)
    .order("ds", { ascending: false })
    .order("score", { ascending: false });

  if (error) {
    console.error("Failed to fetch video rankings: ", error);
    throw new Error("Failed to fetch video rankings: " + error);
  }

  // Create a map to store the latest ranking for each video
  const latestVideoRankings = new Map();
  data.forEach((ranking) => {
    if (!latestVideoRankings.has(ranking.video_id)) {
      latestVideoRankings.set(ranking.video_id, ranking);
    }
  });

  // Convert the map values back to an array
  return Array.from(latestVideoRankings.values());
}
